import { put, call, takeLatest } from 'redux-saga/effects'
import { createSystemNotification } from 'actions/application'
import { NOTIFICATION_SUCCESS, NOTIFICATION_ERROR } from 'utils/constants'
import {
  LOAD_FAST_FEATURE_STATUS,
  LOAD_FAST_FEATURE_STATUS_SUCCESS,
  TOGGLE_FAST_FEATURE_STATUS,
  TOGGLE_FAST_FEATURE_STATUS_SUCCESS,
  TOGGLE_FAST_FEATURE_STATUS_FAILURE,
  SAVE_FAST_FEATURE_DETAILS,
  SAVE_FAST_FEATURE_DETAILS_SUCCESS,
  SAVE_FAST_FEATURE_DETAILS_FAILURE,
} from 'actions/feature/fastFeatures'
import {
  getFastFeatureStatus,
  setFastFeatureStatus,
  saveFastFeatureDetails as saveFastFeatureDetailsService,
} from 'services/fastFeatures'
import strings from 'utils/strings'

// ─── Load Fast Feature Status ─────────────────────────────────────────────────

function* handleLoadFastFeatureStatus({ payload }) {
  const { featureId } = payload
  try {
    const response = yield call(getFastFeatureStatus, { featureId })
    // Support both { data: { isFastFeature } } and { isFastFeature } response shapes
    const isFastFeature = Boolean(
      response?.data?.isFastFeature ?? response?.isFastFeature
    )
    yield put({
      type: LOAD_FAST_FEATURE_STATUS_SUCCESS,
      payload: { featureId, isFastFeature },
    })
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(
      '[FastFeature] Failed to load status for feature',
      featureId,
      err
    )
    yield put({
      type: LOAD_FAST_FEATURE_STATUS_SUCCESS,
      payload: { featureId, isFastFeature: false },
    })
  }
}

export function* watchLoadFastFeatureStatus() {
  yield takeLatest(LOAD_FAST_FEATURE_STATUS, handleLoadFastFeatureStatus)
}

// ─── Notification IDs (stable so repeated actions replace the previous banner) ─

const FF_TOGGLE_SUCCESS_ID = 'FF_TOGGLE_SUCCESS'
const FF_TOGGLE_ERROR_ID = 'FF_TOGGLE_ERROR'
const FF_SAVE_SUCCESS_ID = 'FF_SAVE_SUCCESS'
const FF_SAVE_ERROR_ID = 'FF_SAVE_ERROR'

// ─── Toggle Fast Feature Status ───────────────────────────────────────────────

function* handleToggleFastFeatureStatus({ payload }) {
  const { featureId, isFastFeature } = payload
  try {
    yield call(setFastFeatureStatus, { featureId, isFastFeature })

    yield put({
      type: TOGGLE_FAST_FEATURE_STATUS_SUCCESS,
      payload: { featureId, isFastFeature },
    })

    yield put(
      createSystemNotification({
        notificationMessage: strings.fast_feature_added,
        notificationType: NOTIFICATION_SUCCESS,
        notificationId: FF_TOGGLE_SUCCESS_ID,
        persistent: true,
        closable: true,
      })
    )
  } catch (err) {
    yield put({
      type: TOGGLE_FAST_FEATURE_STATUS_FAILURE,
      payload: { error: err.message || 'Unknown error' },
    })

    yield put(
      createSystemNotification({
        notificationMessage: strings.fast_feature_status_error,
        notificationType: NOTIFICATION_ERROR,
        notificationId: FF_TOGGLE_ERROR_ID,
        persistent: true,
        closable: true,
      })
    )
  }
}

export function* watchToggleFastFeatureStatus() {
  yield takeLatest(TOGGLE_FAST_FEATURE_STATUS, handleToggleFastFeatureStatus)
}

// ─── Save Fast Feature Details ────────────────────────────────────────────────

function* handleSaveFastFeatureDetails({ payload }) {
  const {
    featureId,
    featureNumber,
    description,
    ecuType,
    outputs,
    filterOutput,
  } = payload
  try {
    yield call(saveFastFeatureDetailsService, {
      featureId,
      featureNumber,
      description,
      ecuType,
      outputs,
      filterOutput,
    })

    yield put({
      type: SAVE_FAST_FEATURE_DETAILS_SUCCESS,
      payload: { featureId },
    })

    yield put(
      createSystemNotification({
        notificationMessage: strings.fast_feature_added,
        notificationType: NOTIFICATION_SUCCESS,
        notificationId: FF_SAVE_SUCCESS_ID,
        persistent: true,
        closable: true,
      })
    )
  } catch (err) {
    yield put({
      type: SAVE_FAST_FEATURE_DETAILS_FAILURE,
      payload: { error: err.message || 'Unknown error' },
    })

    yield put(
      createSystemNotification({
        notificationMessage: strings.fast_feature_save_error,
        notificationType: NOTIFICATION_ERROR,
        notificationId: FF_SAVE_ERROR_ID,
        persistent: true,
        closable: true,
      })
    )
  }
}

export function* watchSaveFastFeatureDetails() {
  yield takeLatest(SAVE_FAST_FEATURE_DETAILS, handleSaveFastFeatureDetails)
}
